import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, message, Radio, Space, Table, Typography, Select, Switch, Slider, Modal } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import Map, { Layer, Marker, Source } from 'react-map-gl';
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import {
    buildMissionPatternReturnUrl,
    readMissionPatternHandoff,
    writeMissionPatternResult,
    type MissionPatternHandoffResult,
} from '../components/Operations/missionPatternHandoff';
import { getInvalidPathSegmentIndexes } from '../components/Operations/missionAreaPathValidation';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../style/PatternCreate.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';
const MIN_GRID_SPACING_METERS = 1;
const MAX_GRID_SPACING_METERS = 100;
const MAX_GRID_PREVIEW_POINTS = 500;

const { Title, Text } = Typography;

interface Point {
    index: number;
    lat: number;
    lng: number;
    label?: string;
}

interface AreaInfo {
    id: number;
    name: string;
    code?: string;
    geom?: any;
    spaceType?: string;
}

const PatternCreateArea: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const areaId = searchParams.get('areaId');
    const patternId = searchParams.get('patternId');
    const missionDraftKey = searchParams.get('missionDraftKey');
    const [missionHandoff] = useState(() => readMissionPatternHandoff(missionDraftKey));
    const isMissionRouteFlow = Boolean(missionDraftKey && missionHandoff && !patternId);
    const [effectiveAreaId, setEffectiveAreaId] = useState<string | null>(areaId);
    const [editingPatternId, setEditingPatternId] = useState<number | null>(patternId ? Number(patternId) : null);
    
    const [form] = Form.useForm();
    const [area, setArea] = useState<AreaInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveAsPattern, setSaveAsPattern] = useState(true);
    
    const [mode, setMode] = useState<'manual' | 'grid'>('manual');
    const [points, setPoints] = useState<Point[]>([]);
    
    // Grid configuration
    const [gridSpacing, setGridSpacing] = useState(5);
    const [gridAngle, setGridAngle] = useState(0);
    const [edgeClearance, setEdgeClearance] = useState(2);
    const [gridPattern, setGridPattern] = useState<'rectangular' | 'hexagonal' | 's-pattern'>('rectangular');
    const [gridStartCorner, setGridStartCorner] = useState<'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'>('bottom-left');
    const [gridPrimaryDirection, setGridPrimaryDirection] = useState<'horizontal' | 'vertical'>('horizontal');
    const [showGridPreview, setShowGridPreview] = useState(false);
    const [previewPoints, setPreviewPoints] = useState<Point[]>([]);
    const mapRef = useRef<MapRef>(null);

    const returnToMission = (result: MissionPatternHandoffResult) => {
        if (!missionDraftKey || !writeMissionPatternResult(missionDraftKey, result)) {
            message.error('The mission draft is no longer available. Please return to Operations and start again.');
            return false;
        }
        navigate(buildMissionPatternReturnUrl(missionDraftKey));
        return true;
    };

    const handleCancel = () => {
        if (isMissionRouteFlow) {
            returnToMission({ status: 'cancelled' });
            return;
        }
        navigate('/patterns');
    };

    useEffect(() => {
        if (!effectiveAreaId && !patternId) {
            message.error('Area ID is required');
            navigate('/patterns');
            return;
        }

        const loadArea = async () => {
            if (!effectiveAreaId) return;
            setLoading(true);
            try {
                const res = await axios.get(ENDPOINTS.AREA_DETAIL(parseInt(effectiveAreaId, 10)), { withCredentials: true });
                const data = res.data?.data || res.data;
                if (data.spaceType === 'indoor') {
                    message.error('This is an indoor area. GPS pattern creation requires a GPS-based area.');
                    navigate('/patterns');
                    return;
                }
                setArea(data);
            } catch (err) {
                console.error('Failed to load area', err);
                message.error('Failed to load area information');
                navigate('/patterns');
            } finally {
                setLoading(false);
            }
        };

        loadArea();
    }, [effectiveAreaId, navigate, patternId]);

    useEffect(() => {
        if (!patternId) return;
        const loadPattern = async () => {
            try {
                const res = await axios.get(`${ENDPOINTS.PATTERNS}/${patternId}`, { withCredentials: true });
                const data = res.data?.data || res.data;
                if (!data) return;
                if (data.coordSystem !== 'gps') {
                    message.error('This is not a GPS pattern. Open it in its template or local map editor.');
                    navigate('/patterns');
                    return;
                }
                form.setFieldsValue({
                    name: data.name,
                    type: data.patternType,
                });
                setEditingPatternId(data.id);
                if (!effectiveAreaId && data.area?.id) {
                    setEffectiveAreaId(String(data.area.id));
                }
                const pointsRes = await axios.get(`${ENDPOINTS.PATTERNS}/${patternId}/points`, { withCredentials: true });
                const pointData = pointsRes.data?.data || pointsRes.data || [];
                const mapped = pointData
                    .filter((p: any) => p.gpsLat != null && p.gpsLon != null)
                    .map((p: any, idx: number) => ({
                        index: p.pointIndex ?? idx + 1,
                        lat: p.gpsLat,
                        lng: p.gpsLon,
                        label: p.label || undefined,
                    }));
                setPoints(mapped);
            } catch (err) {
                console.error('Failed to load pattern for edit', err);
                message.error('Failed to load pattern for editing');
                navigate('/patterns');
            }
        };
        loadPattern();
    }, [patternId, form, navigate, effectiveAreaId]);

    const handleMapClick = (lat: number, lng: number) => {
        if (mode !== 'manual') return;
        addPoint(lat, lng);
    };

    const addPoint = (lat: number, lng: number) => {
        const newPoint: Point = {
            index: points.length + 1,
            lat,
            lng,
        };
        setPoints([...points, newPoint]);
    };

    const addManualPoint = () => {
        // Add point at center of area or default location
        if (area?.geom?.coordinates) {
            const coords = area.geom.type === 'Polygon' 
                ? area.geom.coordinates[0] 
                : area.geom.coordinates[0][0];
            
            const lats = coords.map((c: number[]) => c[1]);
            const lngs = coords.map((c: number[]) => c[0]);
            const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
            const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
            
            addPoint(centerLat, centerLng);
        } else {
            addPoint(51.5074, -0.1278); // Default to London
        }
    };

    // Use the generated, polygon-filtered count rather than the bounding-grid size.
    // The extra point lets callers distinguish an allowed preview from an oversized one.
    const estimatePointCount = (): number => {
        return calculateGridPoints(MAX_GRID_PREVIEW_POINTS + 1).length;
    };

    // Update preview with safety check
    useEffect(() => {
        if (mode === 'grid' && showGridPreview && area?.geom) {
            const preview = calculateGridPoints(MAX_GRID_PREVIEW_POINTS + 1);
            if (preview.length > MAX_GRID_PREVIEW_POINTS) {
                message.warning(`More than ${MAX_GRID_PREVIEW_POINTS} points - too many for preview. Increase spacing or disable preview.`);
                setShowGridPreview(false);
                setPreviewPoints([]);
                return;
            }
            setPreviewPoints(preview);
        } else {
            setPreviewPoints([]);
        }
    }, [mode, showGridPreview, gridSpacing, gridAngle, edgeClearance, gridPattern, gridStartCorner, gridPrimaryDirection, area]);

    const generateGrid = () => {
        const newPoints = calculateGridPoints();
        if (newPoints.length === 0) {
            message.warning('No points fit within the area. Try adjusting grid parameters.');
            return;
        }
        setPoints(newPoints);
        message.success(`Generated ${newPoints.length} points`);
    };

    const calculateGridPoints = (pointLimit = Number.POSITIVE_INFINITY): Point[] => {
        if (!area?.geom?.coordinates) {
            return [];
        }

        const coords = area.geom.type === 'Polygon' 
            ? area.geom.coordinates[0] 
            : area.geom.coordinates[0][0];
        
        // Calculate bounding box
        const lats = coords.map((c: number[]) => c[1]);
        const lngs = coords.map((c: number[]) => c[0]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        
        // Convert meters to degrees
        const metersPerDegreeLat = 111320;
        const metersPerDegreeLng = 111320 * Math.cos(centerLat * Math.PI / 180);
        const spacingLat = gridSpacing / metersPerDegreeLat;
        const spacingLng = gridSpacing / metersPerDegreeLng;
        
        // Calculate area dimensions in degrees
        const areaWidth = maxLng - minLng;
        const areaHeight = maxLat - minLat;
        
        // Use square bounding box (size = max dimension) to ensure full coverage at any rotation
        const maxDimension = Math.max(areaWidth, areaHeight);
        const angleRad = gridAngle * Math.PI / 180;
        
        // Number of points needed to cover square area
        const numRows = Math.ceil(maxDimension / spacingLat) + 2;
        const numCols = Math.ceil(maxDimension / spacingLng) + 2;
        
        const tempPoints: Array<{ lat: number; lng: number; row: number; col: number }> = [];
        
        // Generate grid centered on area
        const cosAngle = Math.cos(angleRad);
        const sinAngle = Math.sin(angleRad);
        
        let reachedPointLimit = false;
        for (let i = 0; i < numRows && !reachedPointLimit; i++) {
            for (let j = 0; j < numCols; j++) {
                // Grid in local coordinates (centered at origin)
                const localX = (j - numCols / 2) * gridSpacing;
                const localY = (i - numRows / 2) * gridSpacing;
                
                // Rotate the grid
                const rotatedX = localX * cosAngle - localY * sinAngle;
                const rotatedY = localX * sinAngle + localY * cosAngle;
                
                // Convert back to lat/lng
                const lng = centerLng + rotatedX / metersPerDegreeLng;
                const lat = centerLat + rotatedY / metersPerDegreeLat;
                
                // Check if point is inside polygon with clearance
                if (isPointInPolygon(lat, lng, coords)) {
                    // Check clearance from edges
                    const distanceFromEdge = getDistanceToNearestEdge(lat, lng, coords) * metersPerDegreeLat;
                    if (distanceFromEdge >= edgeClearance) {
                        tempPoints.push({ lat, lng, row: i, col: j });
                        if (tempPoints.length >= pointLimit) {
                            reachedPointLimit = true;
                            break;
                        }
                    }
                }
            }
        }
        
        // Apply ordering based on corner + direction + pattern type
        // In this grid: row=0 is southernmost, col=0 is westernmost
        const newPoints: Point[] = [];
        let index = 1;

        const rowAsc = gridStartCorner === 'bottom-left' || gridStartCorner === 'bottom-right'; // south first
        const colAsc = gridStartCorner === 'bottom-left' || gridStartCorner === 'top-left';     // west first

        if (gridPrimaryDirection === 'horizontal') {
            // Group by rows, sort rows by chosen vertical direction
            const rowGroups: { [key: number]: Array<{ lat: number; lng: number; col: number }> } = {};
            tempPoints.forEach((p) => {
                if (!rowGroups[p.row]) rowGroups[p.row] = [];
                rowGroups[p.row].push(p);
            });
            const sortedRowKeys = Object.keys(rowGroups).map(Number).sort((a, b) => rowAsc ? a - b : b - a);
            sortedRowKeys.forEach((rowNum, outerIdx) => {
                const rowPoints = rowGroups[rowNum].sort((a, b) => colAsc ? a.col - b.col : b.col - a.col);
                const isZigzag = (gridPattern === 's-pattern') && outerIdx % 2 === 1;
                const ordered = isZigzag ? [...rowPoints].reverse() : rowPoints;
                ordered.forEach((p) => newPoints.push({ index: index++, lat: p.lat, lng: p.lng }));
            });
        } else {
            // Group by cols, sort cols by chosen horizontal direction
            const colGroups: { [key: number]: Array<{ lat: number; lng: number; row: number }> } = {};
            tempPoints.forEach((p) => {
                if (!colGroups[p.col]) colGroups[p.col] = [];
                colGroups[p.col].push(p);
            });
            const sortedColKeys = Object.keys(colGroups).map(Number).sort((a, b) => colAsc ? a - b : b - a);
            sortedColKeys.forEach((colNum, outerIdx) => {
                const colPoints = colGroups[colNum].sort((a, b) => rowAsc ? a.row - b.row : b.row - a.row);
                const isZigzag = (gridPattern === 's-pattern') && outerIdx % 2 === 1;
                const ordered = isZigzag ? [...colPoints].reverse() : colPoints;
                ordered.forEach((p) => newPoints.push({ index: index++, lat: p.lat, lng: p.lng }));
            });
        }

        return newPoints;
    };

    // Approximate distance to nearest polygon edge
    const getDistanceToNearestEdge = (lat: number, lng: number, polygon: number[][]): number => {
        let minDist = Infinity;
        for (let i = 0; i < polygon.length - 1; i++) {
            const [x1, y1] = polygon[i];
            const [x2, y2] = polygon[i + 1];
            
            // Point-to-line segment distance
            const A = lng - x1;
            const B = lat - y1;
            const C = x2 - x1;
            const D = y2 - y1;
            
            const dot = A * C + B * D;
            const lenSq = C * C + D * D;
            let param = -1;
            if (lenSq !== 0) param = dot / lenSq;
            
            let xx, yy;
            if (param < 0) {
                xx = x1;
                yy = y1;
            } else if (param > 1) {
                xx = x2;
                yy = y2;
            } else {
                xx = x1 + param * C;
                yy = y1 + param * D;
            }
            
            const dx = lng - xx;
            const dy = lat - yy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            minDist = Math.min(minDist, dist);
        }
        return minDist;
    };

    // Simple point-in-polygon check
    const isPointInPolygon = (lat: number, lng: number, polygon: number[][]): boolean => {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i][0], yi = polygon[i][1];
            const xj = polygon[j][0], yj = polygon[j][1];
            
            const intersect = ((yi > lat) !== (yj > lat))
                && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    };

    const invalidPathSegmentIndexes = useMemo(() => getInvalidPathSegmentIndexes(
        points.map((point) => [point.lng, point.lat] as [number, number]),
        area?.geom
    ), [area?.geom, points]);
    const invalidPathSegmentIndexSet = useMemo(
        () => new Set(invalidPathSegmentIndexes),
        [invalidPathSegmentIndexes]
    );
    const pointPathFeatures = useMemo(() => ({
        type: 'FeatureCollection' as const,
        features: points.slice(0, -1).map((point, index) => ({
            type: 'Feature' as const,
            geometry: {
                type: 'LineString' as const,
                coordinates: [
                    [point.lng, point.lat],
                    [points[index + 1].lng, points[index + 1].lat],
                ],
            },
            properties: { invalid: invalidPathSegmentIndexSet.has(index) },
        })),
    }), [invalidPathSegmentIndexSet, points]);
    const previewPathFeatures = useMemo(() => {
        const invalidPreviewSegments = new Set(getInvalidPathSegmentIndexes(
            previewPoints.map((point) => [point.lng, point.lat] as [number, number]),
            area?.geom
        ));
        return {
            type: 'FeatureCollection' as const,
            features: previewPoints.slice(0, -1).map((point, index) => ({
                type: 'Feature' as const,
                geometry: {
                    type: 'LineString' as const,
                    coordinates: [
                        [point.lng, point.lat],
                        [previewPoints[index + 1].lng, previewPoints[index + 1].lat],
                    ],
                },
                properties: { invalid: invalidPreviewSegments.has(index) },
            })),
        };
    }, [area?.geom, previewPoints]);

    const updatePoint = (index: number, field: 'lat' | 'lng' | 'label', value: any) => {
        setPoints(points.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
    };

    const deletePoint = (index: number) => {
        setPoints(points.filter((_, i) => i !== index).map((p, i) => ({ ...p, index: i + 1 })));
    };

    const clearAll = () => {
        setPoints([]);
    };

    const confirmPatternChange = () => new Promise<boolean>((resolve) => {
        Modal.confirm({
            title: 'Update pattern?',
            content: (
                <div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#cf1322' }}>
                        Updating points will remove existing point links.
                    </div>
                    <Alert
                        type="error"
                        showIcon
                        message="Downstream data will be deleted"
                        description="Reports, conversations, analysis summaries, learning data, and mission points linked to existing points will be removed."
                    />
                    <Alert
                        type="warning"
                        showIcon
                        message="Measurements will be kept but unlinked"
                        description="Existing measurements will remain but their point link will be cleared."
                        style={{ marginTop: 8 }}
                    />
                </div>
            ),
            okText: 'Continue',
            cancelText: 'Cancel',
            okType: 'danger',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

    const handleSave = async () => {
        if (area?.spaceType === 'indoor') {
            message.error('GPS patterns cannot be saved for an indoor area');
            return;
        }
        try {
            const values = saveAsPattern || !isMissionRouteFlow
                ? await form.validateFields()
                : form.getFieldsValue();

            if (points.length === 0) {
                message.error('Add at least one point');
                return;
            }

            if (invalidPathSegmentIndexes.length > 0) {
                message.error('The route leaves the selected area. Move or remove the red path segments before saving.');
                return;
            }

            if (editingPatternId) {
                const confirmed = await confirmPatternChange();
                if (!confirmed) return;
            }

            setSaving(true);

            const resultPoints = points.map((p, i) => ({
                pointIndex: i + 1,
                gpsLat: p.lat,
                gpsLon: p.lng,
                label: p.label || null,
            }));

            if (isMissionRouteFlow && !saveAsPattern) {
                returnToMission({
                    status: 'mission-only',
                    coordSystem: 'gps',
                    definition: { points: points.length },
                    points: resultPoints,
                });
                return;
            }

            const payload = {
                name: values.name,
                patternType: values.type || 'custom',
                coordSystem: 'gps',
                definition: { points: points.length },
                areaId: effectiveAreaId ? parseInt(effectiveAreaId, 10) : undefined,
                isGlobal: false,
            };

            let targetPatternId = editingPatternId;
            if (editingPatternId) {
                await axios.put(`${ENDPOINTS.PATTERNS}/${editingPatternId}`, payload, { withCredentials: true });
            } else {
                const createRes = await axios.post(ENDPOINTS.PATTERNS, payload, { withCredentials: true });
                targetPatternId = createRes.data?.data?.id || createRes.data?.id;
            }

            if (targetPatternId) {
                await axios.post(
                    `${ENDPOINTS.PATTERNS}/${targetPatternId}/points/bulk`,
                    {
                        points: resultPoints,
                        replaceAll: true,
                    },
                    { withCredentials: true }
                );
            }

            message.success(editingPatternId ? 'Pattern updated successfully' : 'Pattern created successfully');
            if (isMissionRouteFlow && targetPatternId) {
                returnToMission({ status: 'saved', patternId: Number(targetPatternId) });
            } else {
                navigate('/patterns');
            }
        } catch (err) {
            console.error('Failed to create pattern', err);
            message.error(editingPatternId ? 'Failed to update pattern' : 'Failed to create pattern');
        } finally {
            setSaving(false);
        }
    };

    const previewPointEstimate = mode === 'grid' && previewPoints.length === 0
        ? estimatePointCount()
        : previewPoints.length;

    return (
        <div className="pattern-create-container pattern-create-area-page">
            <Card>
                <div style={{ marginBottom: 16 }}>
                    <Button className="pattern-create-back-button" icon={<ArrowLeftOutlined />} onClick={handleCancel}>
                        {isMissionRouteFlow ? 'Back to mission' : 'Back to Patterns'}
                    </Button>
                </div>

                <Title level={3}>{editingPatternId ? 'Edit Pattern' : 'Create Pattern'} - {area?.name}</Title>
                <Alert showIcon type="info" style={{ marginBottom: 12 }}
                    message="GPS pattern — fixed points for this area only"
                    description="Points are saved as latitude/longitude, not normalized template positions. This mode supports areas without a template and non-standard shapes or template images. It cannot be reused as a normalized pattern for another area." />
                <Text type="secondary">Area Code: {area?.code || 'N/A'}</Text>

                <div className="pattern-create-layout">
                    <div className="pattern-create-sidebar">
                        <Form form={form} layout="vertical" className="pattern-create-metadata">
                            <Form.Item label="Pattern Name" name="name" rules={saveAsPattern || !isMissionRouteFlow ? [{ required: true }] : []}>
                                <Input
                                    placeholder="e.g., Measurement Grid"
                                    disabled={isMissionRouteFlow && !saveAsPattern}
                                />
                            </Form.Item>
                            <Form.Item label="Pattern Type" name="type">
                                <Input placeholder="e.g., grid, sampling, custom" />
                            </Form.Item>
                        </Form>

                        {isMissionRouteFlow ? (
                            <Alert
                                type="info"
                                showIcon
                                message="Return this route to your mission"
                                description={(
                                    <Space direction="vertical" size={4}>
                                        <Space>
                                            <Switch checked={saveAsPattern} onChange={setSaveAsPattern} />
                                            <Text strong>Save as a reusable pattern</Text>
                                        </Space>
                                        <Text type="secondary">
                                            Enabled by default. Turn it off to keep these points only in the current mission.
                                        </Text>
                                    </Space>
                                )}
                                style={{ marginTop: 16 }}
                            />
                        ) : null}

                        <div className="pattern-create-tool-mode" style={{ marginTop: 24 }}>
                            <Text strong>Tool Mode</Text>
                            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} style={{ marginTop: 8, display: 'block' }}>
                                <Space direction="vertical">
                                    <Radio value="manual">Manual (Click map/Add GPS)</Radio>
                                    <Radio value="grid">Grid Generator</Radio>
                                </Space>
                            </Radio.Group>
                        </div>

                        {mode === 'grid' && (
                            <div className="pattern-create-grid-config" style={{ marginTop: 16 }}>
                                <Text strong>Grid Configuration</Text>
                                <div style={{ marginTop: 4, marginBottom: 8 }}>
                                    <Switch 
                                        checked={showGridPreview} 
                                        onChange={setShowGridPreview}
                                        size="small"
                                    /> <Text style={{ fontSize: 11 }}>Show preview</Text>
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Spacing (m):</Text>
                                            <InputNumber 
                                                min={MIN_GRID_SPACING_METERS}
                                                max={MAX_GRID_SPACING_METERS}
                                                step={0.5}
                                                value={gridSpacing} 
                                                onChange={(v) => setGridSpacing(v || 5)} 
                                                style={{ marginLeft: 8, width: '100px' }} 
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Rotation (°): {gridAngle}</Text>
                                            <Slider 
                                                min={0} 
                                                max={360} 
                                                value={gridAngle} 
                                                onChange={(v) => setGridAngle(v)} 
                                                style={{ marginTop: 4 }}
                                                marks={{ 0: '0°', 45: '45°', 90: '90°', 180: '180°', 270: '270°', 360: '360°' }}
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Edge Clearance (m):</Text>
                                            <InputNumber 
                                                min={0} 
                                                max={20} 
                                                step={0.5}
                                                value={edgeClearance} 
                                                onChange={(v) => setEdgeClearance(v || 2)} 
                                                style={{ marginLeft: 8, width: '100px' }} 
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12 }}>Pattern Order:</Text>
                                            <Select 
                                                value={gridPattern} 
                                                onChange={setGridPattern}
                                                style={{ marginTop: 4, width: '100%' }}
                                                options={[
                                                    { value: 'rectangular', label: 'Rectangular (→→→)' },
                                                    { value: 's-pattern', label: 'S-Pattern (→←→)' },
                                                    { value: 'hexagonal', label: 'Hexagonal' },
                                                ]}
                                            />
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12, fontWeight: 500 }}>Start Corner:</Text>
                                            <Radio.Group
                                                value={gridStartCorner}
                                                onChange={(e) => setGridStartCorner(e.target.value)}
                                                style={{ marginTop: 4, display: 'block' }}
                                            >
                                                <Radio value="top-left" style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>↖ Top-Left (N-W)</Radio>
                                                <Radio value="top-right" style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>↗ Top-Right (N-E)</Radio>
                                                <Radio value="bottom-left" style={{ display: 'block', fontSize: 11, marginBottom: 2 }}>↙ Bottom-Left (S-W)</Radio>
                                                <Radio value="bottom-right" style={{ display: 'block', fontSize: 11 }}>↘ Bottom-Right (S-E)</Radio>
                                            </Radio.Group>
                                        </div>
                                        <div>
                                            <Text style={{ fontSize: 12, fontWeight: 500 }}>Scan Direction:</Text>
                                            <Radio.Group
                                                value={gridPrimaryDirection}
                                                onChange={(e) => setGridPrimaryDirection(e.target.value)}
                                                style={{ marginTop: 4, display: 'block' }}
                                            >
                                                <Radio value="horizontal" style={{ display: 'block', fontSize: 12, marginBottom: 2 }}>Horizontal (row by row)</Radio>
                                                <Radio value="vertical" style={{ display: 'block', fontSize: 12 }}>Vertical (col by col)</Radio>
                                            </Radio.Group>
                                        </div>
                                        <div style={{
                                            background: previewPoints.length > 0 ? '#e6f4ff' : '#f5f5f5',
                                            border: `1px solid ${previewPoints.length > 0 ? '#1890ff' : '#d9d9d9'}`,
                                            borderRadius: 4,
                                            padding: '8px',
                                            marginBottom: 8,
                                            textAlign: 'center'
                                        }}>
                                            {previewPoints.length > 0 ? (
                                                <Text strong style={{ color: '#1890ff' }}>
                                                    Preview: {previewPoints.length} points
                                                </Text>
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {previewPointEstimate > MAX_GRID_PREVIEW_POINTS
                                                        ? `More than ${MAX_GRID_PREVIEW_POINTS} points`
                                                        : `${previewPointEstimate} points`}
                                                </Text>
                                            )}
                                        </div>
                                        <Button type="dashed" onClick={() => {
                                            generateGrid();
                                            setPreviewPoints([]);
                                        }} block style={{ marginBottom: 8 }}>
                                            Preview Grid
                                        </Button>
                                        <Button type="primary" onClick={() => {
                                            generateGrid();
                                            setShowGridPreview(false);
                                        }} block>
                                            Apply Grid
                                        </Button>
                                    </Space>
                                </div>
                            </div>
                        )}

                        {mode === 'manual' && (
                            <div style={{ marginTop: 16 }}>
                                <Button icon={<PlusOutlined />} onClick={addManualPoint} block>
                                    Add Manual Point
                                </Button>
                                <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                                    Click map to add points
                                </Text>
                            </div>
                        )}

                        <div style={{ marginTop: 24 }}>
                            <Button danger onClick={clearAll} block>
                                Clear All Points
                            </Button>
                        </div>
                    </div>

                    <div className="pattern-create-main">
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Interactive Map View</Text>
                        <div className="pattern-create-area-map" style={{ height: '800px', border: '2px solid #d9d9d9', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                            {area?.geom && (
                                <Map
                                    mapboxAccessToken={MAPBOX_TOKEN}
                                    initialViewState={{ 
                                        longitude: area.geom.coordinates[0][0][0],
                                        latitude: area.geom.coordinates[0][0][1],
                                        zoom: 15
                                    }}
                                    style={{ width: '100%', height: '100%' }}
                                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                                    onClick={mode === 'manual' ? (e: MapLayerMouseEvent) => {
                                        const { lng, lat } = e.lngLat;
                                        addPoint(lat, lng);
                                    } : undefined}
                                    cursor={mode === 'manual' ? 'crosshair' : 'default'}
                                >
                                    {/* Area polygon */}
                                    <Source
                                        id="area"
                                        type="geojson"
                                        data={{
                                            type: 'Feature',
                                            geometry: area.geom,
                                            properties: {}
                                        }}
                                    >
                                        <Layer
                                            id="area-fill"
                                            type="fill"
                                            paint={{ 'fill-color': '#2f54eb', 'fill-opacity': 0.1 }}
                                        />
                                        <Layer
                                            id="area-outline"
                                            type="line"
                                            paint={{ 'line-color': '#1d39c4', 'line-width': 2 }}
                                        />
                                    </Source>

                                    {/* Grid preview points with direction indicators */}
                                    {mode === 'grid' && showGridPreview && (
                                        <>
                                            {/* Draw path line showing direction */}
                                            {previewPoints.length > 1 && (
                                                <Source
                                                    id="preview-path"
                                                    type="geojson"
                                                    data={previewPathFeatures}
                                                >
                                                    <Layer
                                                        id="preview-path-line"
                                                        type="line"
                                                        paint={{ 
                                                            'line-color': [
                                                                'case',
                                                                ['boolean', ['get', 'invalid'], false],
                                                                '#f5222d',
                                                                '#1890ff',
                                                            ],
                                                            'line-width': 2,
                                                            'line-opacity': 0.6,
                                                            'line-dasharray': [2, 2]
                                                        }}
                                                    />
                                                </Source>
                                            )}
                                            {previewPoints.map((p, idx) => (
                                                <Marker
                                                    key={`preview-${idx}`}
                                                    longitude={p.lng}
                                                    latitude={p.lat}
                                                    anchor="center"
                                                >
                                                    <div style={{
                                                        width: idx < 3 ? 18 : 12,
                                                        height: idx < 3 ? 18 : 12,
                                                        borderRadius: '50%',
                                                        background: idx === 0 ? '#52c41a' : 'rgba(24, 144, 255, 0.6)',
                                                        border: '2px solid white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 9,
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                    }}>
                                                        {idx < 3 ? p.index : ''}
                                                    </div>
                                                </Marker>
                                            ))}
                                        </>
                                    )}

                                    {points.length > 1 && (
                                        <Source id="pattern-path" type="geojson" data={pointPathFeatures}>
                                            <Layer
                                                id="pattern-path-line"
                                                type="line"
                                                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                                                paint={{
                                                    'line-color': [
                                                        'case',
                                                        ['boolean', ['get', 'invalid'], false],
                                                        '#f5222d',
                                                        '#fa8c16',
                                                    ],
                                                    'line-width': [
                                                        'case',
                                                        ['boolean', ['get', 'invalid'], false],
                                                        5,
                                                        3,
                                                    ],
                                                    'line-opacity': 0.9,
                                                }}
                                            />
                                        </Source>
                                    )}

                                    {/* Actual points */}
                                    {points.map((p, idx) => (
                                        <Marker
                                            key={`point-${idx}`}
                                            longitude={p.lng}
                                            latitude={p.lat}
                                            anchor="center"
                                        >
                                            <div style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                background: '#fa8c16',
                                                border: '2px solid white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 10,
                                                fontWeight: 'bold',
                                                color: 'white',
                                            }}>
                                                {p.index}
                                            </div>
                                        </Marker>
                                    ))}
                                </Map>
                            )}
                            {mode === 'manual' && (
                                <div style={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    background: 'rgba(255,255,255,0.9)',
                                    padding: '6px 12px',
                                    borderRadius: 4,
                                    fontSize: 12,
                                    fontWeight: 500,
                                }}>
                                    Click map to add points
                                </div>
                            )}
                        </div>

                        <div className="pattern-create-points-table" style={{ marginTop: 16 }}>
                            <Text strong>Coordinate Table ({points.length} points)</Text>
                            <Table
                                size="small"
                                dataSource={points}
                                rowKey="index"
                                pagination={false}
                                style={{ marginTop: 8 }}
                                scroll={{ y: 200 }}
                                columns={[
                                    { title: '#', dataIndex: 'index', width: 40 },
                                    {
                                        title: 'Latitude',
                                        dataIndex: 'lat',
                                        width: 120,
                                        render: (val, record, index) => (
                                            <InputNumber
                                                size="small"
                                                value={val}
                                                step={0.00001}
                                                precision={6}
                                                onChange={(v) => updatePoint(index, 'lat', v || 0)}
                                                style={{ width: '100%' }}
                                            />
                                        ),
                                    },
                                    {
                                        title: 'Longitude',
                                        dataIndex: 'lng',
                                        width: 120,
                                        render: (val, record, index) => (
                                            <InputNumber
                                                size="small"
                                                value={val}
                                                step={0.00001}
                                                precision={6}
                                                onChange={(v) => updatePoint(index, 'lng', v || 0)}
                                                style={{ width: '100%' }}
                                            />
                                        ),
                                    },
                                    {
                                        title: 'Label',
                                        dataIndex: 'label',
                                        render: (val, record, index) => (
                                            <Input
                                                size="small"
                                                value={val}
                                                onChange={(e) => updatePoint(index, 'label', e.target.value)}
                                                placeholder="optional"
                                            />
                                        ),
                                    },
                                    {
                                        title: '',
                                        width: 50,
                                        render: (_, record, index) => (
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => deletePoint(index)}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </div>

                        {invalidPathSegmentIndexes.length > 0 ? (
                            <Alert
                                type="error"
                                showIcon
                                style={{ marginTop: 16 }}
                                message={`${invalidPathSegmentIndexes.length} path segment${invalidPathSegmentIndexes.length === 1 ? '' : 's'} leave the selected area`}
                                description="Move, reorder, or remove points until every red segment stays inside the area. Saving is disabled while the route is invalid."
                            />
                        ) : null}

                        <div className="pattern-create-actions" style={{ marginTop: 24, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={handleCancel}>Cancel</Button>
                                <Button
                                    type="primary"
                                    onClick={handleSave}
                                    loading={saving}
                                    disabled={invalidPathSegmentIndexes.length > 0}
                                >
                                    {editingPatternId
                                        ? 'Save Changes'
                                        : isMissionRouteFlow && !saveAsPattern
                                            ? 'Use in this mission only'
                                            : isMissionRouteFlow
                                                ? 'Save pattern and continue'
                                                : 'Save Pattern'}
                                </Button>
                            </Space>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default PatternCreateArea;
